// src/App.jsx
import "./App.css";
import "./variable.css";
import { useEffect, useRef, useState } from "react";
import {loadGenres} from "./services/genreService";
import MovieGrid from "./components/MovieGrid/MovieGrid";
import Loading from "./components/loading/loading";
import FilterDialog from "./components/FilterDialog/FilterDialog";
import filterIcon from "./assets/filter.svg";
import DetailedView from "./components/DetailedView/DetailedView";

const BASE_URL = "https://api.themoviedb.org/3";
const API_KEY = "b5f2064412eb9f6aead2cb4f127f5b16";
const BEARER =
    "eyJhbGciOiJIUzI1NiJ9.eyJhdWQiOiJiNWYyMDY0NDEyZWI5ZjZhZWFkMmNiNGYxMjdmNWIxNiIsIm5iZiI6MTc2NTAxNjE2Ni4wMzIsInN1YiI6IjY5MzQwMjY2MGE2NjFkNjNkNmI3MGRiNiIsInNjb3BlcyI6WyJhcGlfcmVhZCJdLCJ2ZXJzaW9uIjoxfQ.WJIvF2pI45Pr_KeVmP30y5BJsKafjJrOY1JW2jMjXGc";

function useDebouncedValue(value, delay = 500) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function App() {
  // search input + debounce → query.term
  const [input, setInput] = useState("");
  const inputRef = useRef(null);
  const debounced = useDebouncedValue(input, 500);

   const [detailOpen, setDetailOpen] = useState(false);
   const [selectedMovie, setSelectedMovie] = useState(null);

  // boot flag for genres (if you use names elsewhere)
  const [genresLoaded, setGenresLoaded] = useState(false);

  // unified query object
  const [query, setQuery] = useState({
    term: "",
    filters: { year: "", ratingGte: "", genres: "" },
    page: 1,
  });

  // results state
  const [movies, setMovies] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // filter dialog state
  const [filtersOpen, setFiltersOpen] = useState(false);
  const openFilters = () => setFiltersOpen(true);
  const closeFilters = () => setFiltersOpen(false);
  const saveFilters = (f) => {
    setQuery((prev) => ({ ...prev, filters: f, page: 1 }));
  };

  // promote debounced input to query.term (≥3 chars or cleared)
  useEffect(() => {
    const trimmed = debounced.trim();
    setQuery((prev) => ({
      ...prev,
      term: trimmed.length >= 3 ? trimmed : "",
      page: 1,
    }));
  }, [debounced]);

  // load TMDB genres once
  useEffect(() => {
    (async () => {
      await loadGenres(API_KEY);
      setGenresLoaded(true);
    })();
  }, []);

  async function fetchMovies({ q, p, filters }) {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        api_key: API_KEY,
        language: "en-US",
        page: String(p),
        include_adult: "false",
      });

      const isSearch = q && q.trim();
      if (isSearch) {
        params.set("query", q.trim());
      } else {
        // filters.genres is already a CSV of IDs (e.g., "28,12,35")
        if (filters?.year) params.set("primary_release_year", String(filters.year));
        if (filters?.ratingGte) params.set("vote_average.gte", String(filters.ratingGte));
        if (filters?.genres) params.set("with_genres", String(filters.genres));
        params.set("sort_by", "popularity.desc");
      }

      const endpoint = isSearch ? "search/movie" : "discover/movie";
      const url = `${BASE_URL}/${endpoint}?${params.toString()}`;

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${BEARER}`,
          "Content-Type": "application/json;charset=utf-8",
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      setMovies(data.results || []);
      setTotalPages(Math.max(1, data.total_pages || 1));
    } catch (e) {
      setMovies([]);
      setTotalPages(1);
      setError(e.message || "Failed to load movies.");
    } finally {
      setLoading(false);
    }
  }

  // fetch whenever the unified query changes
  useEffect(() => {
    (async () => {
      await fetchMovies({ q: query.term, p: query.page, filters: query.filters });
    })();
  }, [query]); // single source of truth

  const onPageChange = (nextPage) =>
      setQuery((prev) => ({ ...prev, page: nextPage }));

  const clearInput = () => {
    setInput("");
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  useEffect(() => {
    document.body.style.overflow = detailOpen ? "hidden" : "";
       return () => { document.body.style.overflow = ""; };
     }, [detailOpen]);
   const openDetail = (m) => {
       setSelectedMovie(m);
       setDetailOpen(true);
     };

  const isFiltered = !!(
      query.filters?.year ||
      query.filters?.ratingGte ||
      (query.filters?.genres && String(query.filters.genres).trim() !== "")
  );

  return (
      <div className="App-root">
        <header className="app-header">
          <div className="container">
            <h1 style={{ margin: 0 }}>🎬 Movie Explorer</h1>
            <div className="hstack" style={{ marginTop: "0.75rem", position: "relative" }}>
              <input
                  ref={inputRef}
                  className="input search-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Search movies… (type at least 3 characters)"
                  aria-label="Search movies"
                  style={{ paddingRight: 84 }} // room for clear + filter
              />
              {input.length > 0 && (
                  <button
                      type="button"
                      className="search-clear-btn"
                      onClick={clearInput}
                      aria-label="Clear search"
                      title="Clear"
                  >
                    X
                  </button>
              )}
              <img onClick={openFilters}  className={`search-filter-icon ${isFiltered ? "is-active" : ""}`} src={filterIcon} alt="" aria-hidden="true" />
            </div>
          </div>
        </header>

        <main className="app-container" style={{ paddingTop: 16 }}>
          {genresLoaded ? (
              <MovieGrid
                  movies={movies}
                  page={query.page}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                  loading={loading}
                  error={error}
                  onCardClick={openDetail}
              />
          ) : (
              <Loading />
          )}
        </main>

        <DetailedView
            open={detailOpen}
            movie={selectedMovie}
            onClose={() => setDetailOpen(false)}
            apiKey={API_KEY}
            bearer={BEARER}
        />

        {/* Filter dialog */}
        <FilterDialog
            open={filtersOpen}
            initialFilters={query.filters}
            onClose={closeFilters}
            onSave={saveFilters}
        />
      </div>
  );
}
